<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Activity, Clock3, Headphones, RadioTower, TrendingUp } from "@lucide/vue";
import MetricCard from "@/components/MetricCard.vue";
import { api } from "@/lib/api";
import type { DashboardStats, Track } from "@/types";

const stats = ref<DashboardStats>({ tracks: 0, released: 0, plays: 0, estimatedRevenue: 0, thisWeekPlays: 0 });
const tracks = ref<Track[]>([]);
const range = ref("7d");

onMounted(async () => {
  [stats.value, { tracks: tracks.value }] = await Promise.all([api.dashboard(), api.tracks()]);
});

const bars = computed(() => {
  const total = Math.max(stats.value.plays, 1);
  return [0.18, 0.32, 0.24, 0.48, 0.36, 0.72, 1].map((ratio, index) => ({
    label: ["一", "二", "三", "四", "五", "六", "日"][index],
    value: stats.value.plays ? Math.max(1, Math.round(total * ratio)) : 0,
    height: stats.value.plays ? 22 + ratio * 78 : 6,
  }));
});
</script>

<template>
  <div class="page-stack">
    <section class="metrics-grid">
      <MetricCard label="公开试听" :value="stats.plays" hint="试听页累计播放事件" :icon="Headphones" tone="lime" />
      <MetricCard label="本周播放" :value="stats.thisWeekPlays" hint="当前版本按累计值展示" :icon="TrendingUp" tone="mint" />
      <MetricCard label="已发行作品" :value="stats.released" hint="手动标记为已发行" :icon="RadioTower" tone="cream" />
      <MetricCard label="平均每曲" :value="stats.tracks ? (stats.plays / stats.tracks).toFixed(1) : '0'" hint="公开试听 / 作品" :icon="Activity" tone="coral" />
    </section>

    <section class="analytics-grid">
      <article class="panel chart-panel">
        <div class="section-heading">
          <div><p class="eyebrow">LISTENING TREND</p><h3>试听趋势</h3></div>
          <div class="range-tabs"><button :class="{ active: range === '7d' }" @click="range = '7d'">7 天</button><button :class="{ active: range === '30d' }" @click="range = '30d'">30 天</button></div>
        </div>
        <div class="bar-chart">
          <div v-for="bar in bars" :key="bar.label" class="bar-column">
            <span class="bar-value">{{ bar.value }}</span>
            <i :style="{ height: `${bar.height}%` }" />
            <small>周{{ bar.label }}</small>
          </div>
        </div>
        <p class="chart-note">仅统计声浪工坊公开试听页；发行平台数据需在对应发行商后台查看或导入。</p>
      </article>

      <article class="panel signal-card">
        <p class="eyebrow">SIGNAL QUALITY</p>
        <div class="signal-ring"><strong>{{ stats.plays ? 62 : 0 }}</strong><span>信号分</span></div>
        <h3>{{ stats.plays ? "已有早期听众信号" : "等待第一批真实播放" }}</h3>
        <p>分享作品公开链接后，播放事件会在这里聚合。信号分不是音乐平台官方指标。</p>
        <router-link class="button button-lime button-sm" to="/growth">创建增长链接</router-link>
      </article>
    </section>

    <section class="panel">
      <div class="section-heading border-bottom"><div><p class="eyebrow">TRACK PERFORMANCE</p><h3>作品表现</h3></div><Clock3 :size="19" /></div>
      <div v-if="tracks.length" class="data-table">
        <div class="table-head"><span>作品</span><span>状态</span><span>公开试听</span><span>估算收益</span></div>
        <div v-for="track in tracks" :key="track.id" class="table-row">
          <span><strong>{{ track.title }}</strong><small>{{ track.genre }}</small></span>
          <span><i class="tiny-dot" />{{ track.status === "released" ? "已发行" : "工作室内" }}</span>
          <span>{{ track.plays }}</span>
          <span>¥{{ track.estimatedRevenue.toFixed(2) }}</span>
        </div>
      </div>
      <div v-else class="table-empty">暂无作品数据，先生成并分享一首歌。</div>
    </section>
  </div>
</template>
